import React from 'react';
import {Typography, Grid} from '@mui/material';
import {Summarize} from '@mui/icons-material';
import {
    RnaPairedSampleTemplate,
    RnaSingleSampleTemplate,
    ChipPairedSampleTemplate,
    ChipSingleSampleTemplate,
    ChipPairedSampleFilenamesTemplate,
    ChipSingleSampleFilenamesTemplate,
    AtacSampleTemplate,
    AtacSampleFilenamesTemplate
} from './SampleTemplate';

/**
 * Downloads a sample CSV template by creating a Blob and a temporary
 * `<a>`.
 */
const handleDownloadClick = (content: string, filename: string) => {
    const blob = new Blob([content], {type: 'text/csv'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
};

interface DownloadCsvContainerProps {
    template: string;
    filename: string;
    description: string;
}

const DownloadCsvContainer = ({template, filename, description}: DownloadCsvContainerProps) => {
    return (
        <Grid
            onClick={() => handleDownloadClick(template, filename)}
            border="1px solid"
            borderColor="outline-variant"
            display="flex"
            borderRadius="12px"
            margin="2% 0"
            padding="2%"
            sx={{cursor: 'pointer', '&:hover': {backgroundColor: 'surface-container-low'}}}>
            <Grid item>
                <Summarize sx={{color: 'on-surface', fontSize: '60px', mx: '15px'}} />
            </Grid>
            <Grid item sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                <Typography variant="title" size="medium">
                    {description}
                </Typography>
                <Typography variant="body2">CSV 1 KB</Typography>
            </Grid>
        </Grid>
    );
};

interface DownloadCsvTemplateProps {
    sequencingType: string;
    sequencingReadType: 'paired' | 'singled';
}

/**
 * Enables downloading a sample CSV template for the experiment.
 *
 * For RNA Seq, this is a single template. For ChIP Seq, there is a
 * experimental design template and a filenames template.
 */
export const DownloadCsvTemplate = ({sequencingType, sequencingReadType}: DownloadCsvTemplateProps) => {
    const isPaired = sequencingReadType === 'paired';

    if (sequencingType === 'RNA Seq') {
        return (
            <DownloadCsvContainer
                template={isPaired ? RnaPairedSampleTemplate : RnaSingleSampleTemplate}
                filename={`rna-seq-template-for-${isPaired ? 'paired' : 'single'}-end-data.csv`}
                description="Download our template — use this file to correctly format your experimental design."
            />
        );
    } else if (sequencingType === 'ChIP Seq') {
        return (
            <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                    <DownloadCsvContainer
                        template={isPaired ? ChipPairedSampleTemplate : ChipSingleSampleTemplate}
                        filename={`chip-seq-metadata-template-for-${isPaired ? 'paired' : 'single'}-end-data.csv`}
                        description="Download our template — use this file to correctly format your experimental design."
                    />
                </Grid>
                <Grid item xs={6}>
                    <DownloadCsvContainer
                        template={isPaired ? ChipPairedSampleFilenamesTemplate : ChipSingleSampleFilenamesTemplate}
                        filename={`chip-seq-files-template-for-${isPaired ? 'paired' : 'single'}-end-data.csv`}
                        description="Download our template — use this file to correctly format your list of filenames."
                    />
                </Grid>
            </Grid>
        );
    } else if (sequencingType === 'ATAC Seq') {
        return (
            <Grid container direction="row" spacing={2}>
                <Grid item xs={6}>
                    <DownloadCsvContainer
                        template={AtacSampleTemplate}
                        filename="atac-sample-template.csv"
                        description="Download our template - use this file to correctly format your experimental design."
                    />
                </Grid>
                <Grid item xs={6}>
                    <DownloadCsvContainer
                        template={AtacSampleFilenamesTemplate}
                        filename="atac-sample-filenames-template.csv"
                        description="Download our template - use this file to correctly format your list of filenames."
                    />
                </Grid>
            </Grid>
        );
    }
    return null;
};
